import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { SUBSCRIPTION_ACKNOWLEDGMENT_TEXT } from '@perdiem/trpc/legal';
import { Button } from './Button';
import { WebHead } from './WebHead';
import { useColors } from '../lib/colors';
import {
  addEntitlementStatusListener,
  configureRevenueCat,
  getPackages,
  isUserCancelledError,
  purchase,
  waitForActiveEntitlement,
  type RCPackage,
} from '../lib/purchases';
import {
  getDefaultSubscriptionPackageId,
  getFreeTrialPaywallCopy,
  getSubscriptionPackagePresentation,
  orderSubscriptionPackagesForPaywall,
} from '../lib/subscription-presentation';
import { supabase } from '../lib/supabase';
import { trpc } from '../lib/trpc';
import { getLegalClientContext } from '../lib/legal-client';
import { getLegalUrl, openExternalUrl } from '../lib/utils';
import { trackSubscriptionConversionEvent } from '../lib/product-events';
import { performSignOut } from '../lib/sign-out';
import { useUnregisterPushNotifications } from '../hooks/usePushNotifications';

// The card-upfront trial offering (RevenueCat: "free_trial"). Settings keeps
// using "default" (no trial) so lapsed households can't claim a second trial.
const OFFERING_IDENTIFIER = 'free_trial';
const DEFAULT_OFFERING_IDENTIFIER = 'default';
const PURCHASE_RECOVERY_WAIT = { attempts: 15, delayMs: 1_000, forceRefresh: true } as const;
const TERMS_URL = getLegalUrl('terms');
const PRIVACY_URL = getLegalUrl('privacy');

export function TrialActivationGate({ onActivated }: { onActivated: () => void }) {
  const c = useColors();
  const router = useRouter();
  const utils = trpc.useUtils();
  const unregisterPushNotifications = useUnregisterPushNotifications();
  const acceptTerms = trpc.legal.acceptSubscriptionTerms.useMutation();
  const syncEntitlement = trpc.subscription.syncEntitlement.useMutation();
  const [packages, setPackages] = useState<RCPackage[]>([]);
  const [offerKind, setOfferKind] = useState<'trial' | 'subscription' | null>(null);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [acceptedRenewal, setAcceptedRenewal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activationInFlight = useRef(false);
  const purchaseTracked = useRef(false);

  const selectedPackage = packages.find((pkg) => pkg.id === selectedPackageId) ?? null;
  const isTrialOffer = offerKind === 'trial';
  const paymentMethodLabel = Platform.OS === 'web' ? 'Card' : 'Payment method';
  const trialCopy = isTrialOffer && selectedPackage
    ? getFreeTrialPaywallCopy(selectedPackage, paymentMethodLabel)
    : null;
  const presentation = selectedPackage
    ? getSubscriptionPackagePresentation(selectedPackage, packages)
    : null;

  const track = (
    eventName: Parameters<typeof trackSubscriptionConversionEvent>[0]['eventName'],
    details: Partial<Parameters<typeof trackSubscriptionConversionEvent>[0]> = {},
  ) => {
    trackSubscriptionConversionEvent({
      eventName,
      trigger: 'onboarding_trial_gate',
      accessKind: 'none',
      isAccessRestricted: true,
      isPromoTrial: false,
      ...details,
    });
  };

  const finishActivation = async (revenueCatCustomerId?: string) => {
    if (activationInFlight.current) return;
    activationInFlight.current = true;
    try {
      if (revenueCatCustomerId) {
        await syncEntitlement.mutateAsync({ revenueCatCustomerId });
      }
      await Promise.all([
        utils.bootstrap.get.invalidate(),
        utils.households.get.invalidate(),
        utils.subscription.getStatus.invalidate(),
      ]);
      if (!purchaseTracked.current) {
        purchaseTracked.current = true;
        track('subscription.purchase_completed', {
          packageId: selectedPackage?.id ?? null,
          packageType: selectedPackage?.packageType ?? null,
        });
      }
      onActivated();
    } catch (activationError) {
      activationInFlight.current = false;
      setError(activationError instanceof Error
        ? activationError.message
        : 'Your purchase succeeded, but Per Diem could not refresh access. Please try again.');
    }
  };

  useEffect(() => {
    let cancelled = false;
    track('subscription.paywall_opened');

    void (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const userId = data.session?.user.id;
        if (!userId) throw new Error('Your session expired. Sign in again to continue.');
        configureRevenueCat(userId);
        const availablePackages = await getPackages({ offeringIdentifier: OFFERING_IDENTIFIER });
        if (cancelled) return;

        const eligibleTrialPackages = availablePackages.filter((pkg) => pkg.freeTrial !== null);
        let displayedPackages = eligibleTrialPackages;
        let displayedOfferKind: 'trial' | 'subscription' = 'trial';

        if (eligibleTrialPackages.length === 0) {
          track('subscription.paywall_packages_failed', {
            packageCount: availablePackages.length,
            failureStage: 'trial_eligibility',
            errorBucket: availablePackages.length === 0 ? 'empty_trial_offering' : 'no_eligible_trial',
          });
          displayedPackages = await getPackages({
            offeringIdentifier: DEFAULT_OFFERING_IDENTIFIER,
          });
          displayedOfferKind = 'subscription';
          if (cancelled) return;
        }

        const orderedPackages = orderSubscriptionPackagesForPaywall(displayedPackages);
        setPackages(orderedPackages);
        setOfferKind(displayedOfferKind);
        const defaultPackageId = getDefaultSubscriptionPackageId(orderedPackages);
        setSelectedPackageId(defaultPackageId);

        if (orderedPackages.length === 0) {
          setError('Subscription options are temporarily unavailable. Please try again shortly.');
          track('subscription.paywall_packages_failed', {
            packageCount: 0,
            failureStage: 'fallback_offering_load',
            errorBucket: 'empty_default_offering',
          });
          return;
        }

        track('subscription.paywall_packages_loaded', { packageCount: orderedPackages.length });
        const defaultPackage = orderedPackages.find((pkg) => pkg.id === defaultPackageId);
        if (defaultPackage) {
          track('subscription.paywall_plan_selected', {
            packageId: defaultPackage.id,
            packageType: defaultPackage.packageType,
            selectionSource: 'default',
          });
        }
      } catch (loadError) {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : 'Could not load subscription options.');
        track('subscription.paywall_packages_failed', {
          failureStage: 'offering_load',
          errorBucket: 'package_load_error',
        });
      } finally {
        if (!cancelled) setLoadingPackages(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  useEffect(() => addEntitlementStatusListener((status) => {
    if (!status.isActive || activationInFlight.current) return;
    void supabase.auth.getSession().then(({ data }) => {
      void finishActivation(data.session?.user.id);
    });
  }), [selectedPackage]);

  const handlePurchase = async () => {
    if (!selectedPackage) {
      setError('Choose a subscription plan to continue.');
      return;
    }
    if (!acceptedRenewal) {
      setError('Please acknowledge the trial and auto-renewal terms to continue.');
      return;
    }

    setPurchasing(true);
    setError(null);
    try {
      track('subscription.checkout_started', {
        packageId: selectedPackage.id,
        packageType: selectedPackage.packageType,
      });
      await acceptTerms.mutateAsync(getLegalClientContext('subscription'));

      const { data } = await supabase.auth.getSession();
      const userId = data.session?.user.id;
      if (!userId) throw new Error('Your session expired. Sign in again to continue.');

      const existing = await waitForActiveEntitlement({ attempts: 1, delayMs: 0 });
      if (existing.isActive) {
        await finishActivation(userId);
        return;
      }

      const result = await purchase(selectedPackage.id, data.session?.user.email ?? undefined);
      if (!result.isActive) {
        const recovered = await waitForActiveEntitlement(PURCHASE_RECOVERY_WAIT);
        if (!recovered.isActive) throw new Error('The subscription did not activate. Please try again.');
      }
      await finishActivation(result.revenueCatCustomerId);
    } catch (purchaseError) {
      if (isUserCancelledError(purchaseError)) {
        track('subscription.checkout_canceled', {
          packageId: selectedPackage.id,
          packageType: selectedPackage.packageType,
          failureStage: 'checkout',
          errorBucket: 'user_canceled',
        });
      } else {
        setError(purchaseError instanceof Error ? purchaseError.message : 'Could not start your subscription.');
        track('subscription.checkout_failed', {
          packageId: selectedPackage.id,
          packageType: selectedPackage.packageType,
          failureStage: 'checkout',
          errorBucket: 'purchase_error',
        });
      }
    } finally {
      setPurchasing(false);
    }
  };

  const valueProps = useMemo(() => [
    'Connect your accounts once and keep them updating automatically',
    'Know what you can spend today without losing sight of tomorrow',
    'See your financial-independence plan update as your life changes',
    'Ask questions using your real financial picture',
  ], []);

  return (
    <SafeAreaView className="flex-1 bg-canvas dark:bg-canvas-dark">
      <WebHead>
        <title>{isTrialOffer ? 'Start Your Free Trial – Per Diem' : 'Subscribe to Per Diem'}</title>
      </WebHead>
      <ScrollView contentContainerStyle={{ flexGrow: 1, paddingBottom: 32 }}>
        <View className="mx-auto w-full max-w-[560px] flex-1 px-6 py-5">
          <View className="mb-6 flex-row items-center justify-between">
            <Text className="text-lg font-bold text-forest-700 dark:text-forest-500">Per Diem</Text>
            <Pressable
              hitSlop={8}
              onPress={() => {
                void performSignOut({
                  source: 'trial_activation_gate',
                  route: '/(onboarding)',
                  unregisterPushNotifications,
                }).then(() => {
                  router.replace('/(auth)/login');
                });
              }}
            >
              <Text className="text-xs text-gray-400 dark:text-gray-500">Sign out</Text>
            </Pressable>
          </View>

          <View className="items-center">
            <View className="mb-4 rounded-full bg-green-100 px-3 py-1.5 dark:bg-green-900/40">
              <Text className="text-xs font-semibold text-forest-700 dark:text-forest-500">
                {offerKind === null
                  ? 'Checking available plans'
                  : isTrialOffer
                    ? trialCopy?.badgeLabel ?? 'Free trial · $0 today'
                    : 'Your progress is saved'}
              </Text>
            </View>
            <Text className="text-center text-3xl font-bold leading-10 text-gray-900 dark:text-gray-100">
              {trialCopy?.heading ?? (isTrialOffer ? 'Start your free trial' : 'Pick up right where you left off')}
            </Text>
            <Text className="mt-3 text-center text-base leading-6 text-gray-600 dark:text-gray-300">
              {trialCopy
                ? trialCopy.description
                : 'Choose a plan to continue building your financial-independence plan.'}
            </Text>
            {trialCopy ? (
              <Text className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
                {trialCopy.paymentMethodNote}
              </Text>
            ) : null}
          </View>

          <View className="my-6 gap-3 rounded-2xl bg-white p-5 shadow-sm dark:bg-gray-900">
            {valueProps.map((item) => (
              <View key={item} className="flex-row items-start gap-3">
                <Ionicons name="checkmark-circle" size={19} color={c.primary} style={{ marginTop: 1 }} />
                <Text className="flex-1 text-sm leading-6 text-gray-700 dark:text-gray-200">{item}</Text>
              </View>
            ))}
          </View>

          {loadingPackages ? <ActivityIndicator color={c.primary} className="my-8" /> : null}

          <View className="gap-3">
            {packages.map((pkg) => {
              const isSelected = pkg.id === selectedPackageId;
              const pkgPresentation = getSubscriptionPackagePresentation(pkg, packages);
              return (
                <Pressable
                  key={pkg.id}
                  disabled={purchasing}
                  onPress={() => {
                    setSelectedPackageId(pkg.id);
                    setError(null);
                    track('subscription.paywall_plan_selected', {
                      packageId: pkg.id,
                      packageType: pkg.packageType,
                      selectionSource: 'user',
                    });
                  }}
                  className={`rounded-2xl border p-4 ${isSelected
                    ? 'border-forest-500 bg-green-50 dark:bg-green-950/50'
                    : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'}`}
                >
                  <View className="flex-row items-center justify-between gap-4">
                    <View className="flex-1">
                      <View className="flex-row items-center gap-2">
                        <Text className="text-base font-semibold text-gray-900 dark:text-gray-100">
                          {pkgPresentation.label}
                        </Text>
                        {pkgPresentation.badge ? (
                          <View className="rounded-full bg-green-100 px-2 py-0.5 dark:bg-green-900/40">
                            <Text className="text-[11px] font-semibold text-forest-700 dark:text-forest-500">
                              {pkgPresentation.badge}
                            </Text>
                          </View>
                        ) : null}
                      </View>
                      {pkgPresentation.comparisonLabel ? (
                        <Text className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {pkgPresentation.comparisonLabel}
                        </Text>
                      ) : null}
                      {pkgPresentation.description ? (
                        <Text className="mt-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                          {pkgPresentation.description}
                        </Text>
                      ) : null}
                    </View>
                    <View className="items-end gap-2">
                      <Text className="text-lg font-bold text-green-700 dark:text-green-500">
                        {pkgPresentation.priceLabel}
                      </Text>
                      <View className={`h-6 w-6 items-center justify-center rounded-full border ${isSelected
                        ? 'border-forest-500 bg-forest-600'
                        : 'border-gray-300 dark:border-gray-600'}`}>
                        {isSelected ? <Ionicons name="checkmark" size={15} color="#ffffff" /> : null}
                      </View>
                    </View>
                  </View>
                </Pressable>
              );
            })}
          </View>

          {error ? (
            <View className="mt-4 rounded-xl bg-red-50 px-4 py-3 dark:bg-red-950/30">
              <Text className="text-sm text-red-700 dark:text-red-300">{error}</Text>
            </View>
          ) : null}

          {selectedPackage && presentation ? (
            <>
              <Pressable
                onPress={() => {
                  setAcceptedRenewal((value) => !value);
                  setError(null);
                }}
                className="mt-5 flex-row gap-3 rounded-xl bg-gray-100 px-4 py-3 dark:bg-gray-800"
              >
                <View className={`mt-0.5 h-5 w-5 items-center justify-center rounded border ${acceptedRenewal
                  ? 'border-forest-500 bg-forest-600'
                  : 'border-gray-400'}`}>
                  {acceptedRenewal ? <Ionicons name="checkmark" size={14} color="#ffffff" /> : null}
                </View>
                <Text className="flex-1 text-xs leading-5 text-gray-600 dark:text-gray-300">
                  {trialCopy
                    ? trialCopy.acknowledgment
                    : SUBSCRIPTION_ACKNOWLEDGMENT_TEXT}
                </Text>
              </Pressable>

              <Button
                size="lg"
                className="mt-4"
                loading={purchasing}
                disabled={!acceptedRenewal || loadingPackages}
                onPress={() => { void handlePurchase(); }}
              >
                {trialCopy?.ctaLabel ?? 'Subscribe and continue'}
              </Button>
              <Text className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
                {trialCopy
                  ? trialCopy.reassurance
                  : 'You’ll be charged today. Cancel anytime from Settings.'}
              </Text>
            </>
          ) : null}

          <View className="mt-5 flex-row items-center justify-center gap-5">
            <Pressable onPress={() => openExternalUrl(TERMS_URL)}>
              <Text className="text-xs font-medium text-green-700">Terms</Text>
            </Pressable>
            <Pressable onPress={() => openExternalUrl(PRIVACY_URL)}>
              <Text className="text-xs font-medium text-green-700">Privacy</Text>
            </Pressable>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
